import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { 
  GraduationCap, 
  Mail, 
  Lock, 
  Smartphone, 
  Send, 
  CheckCircle,
  ArrowLeft,
  Shield,
  Users,
  Award,
  Copy,
} from "lucide-react";
import { useBranding } from "@/context/BrandingContext";
import CountryCodePicker, { DEFAULT_COUNTRY } from "@/components/common/CountryCodePicker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const roleLabel = (role) => ({
  super_admin: "Super Admin", chain_admin: "Chain Admin", institute_admin: "School Admin",
  teacher: "Teacher", student: "Student", parent: "Parent", driver: "Driver", staff: "Staff",
  training_manager: "Training Manager",
}[role] || String(role || "User").replace(/_/g, " "));

const Login = ({ setUser }) => {
  const [loginMethod, setLoginMethod] = useState("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mobile, setMobile] = useState("");
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [mockOtp, setMockOtp] = useState("");
  const [smsSent, setSmsSent] = useState(false);
  const [smsError, setSmsError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [loading, setLoading] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [showReset, setShowReset] = useState(false);
  // Multi-account picker: when a phone is shared by multiple roles/accounts,
  // the backend returns { multiple_accounts: true, accounts: [...] } and we
  // ask the user to pick one before OTP is issued.
  const [multipleAccounts, setMultipleAccounts] = useState(null);
  const [selectedRole, setSelectedRole] = useState("");
  const navigate = useNavigate();
  const { branding } = useBranding();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password,
      });

      const { access_token, user } = response.data;
      localStorage.setItem("token", access_token);
      localStorage.setItem("user", JSON.stringify(user));
      setUser(user);

      toast.success(`Login successful — ${roleLabel(user.role)} portal`);

      switch (user.role) {
        case "super_admin":
          navigate("/super-admin");
          break;
        case "chain_admin":
          navigate("/chain-admin");
          break;
        case "institute_admin":
          navigate("/school-admin");
          break;
        case "teacher":
          navigate("/teacher");
          break;
        case "student":
          navigate("/student");
          break;
        case "parent":
          navigate("/parent");
          break;
        case "staff":
          navigate("/staff");
          break;
        case "driver":
          navigate("/driver");
          break;
        case "training_manager":
          navigate("/training-manager");
          break;
        default:
          navigate("/");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  // Full E.164 phone we send to the backend (country dial + national number)
  const e164 = `${country?.dial || "+91"}${(mobile || "").replace(/[^0-9]/g, "")}`;

  const handleSendOTP = async (e, roleOverride) => {
    if (e && e.preventDefault) e.preventDefault();
    setLoading(true);

    try {
      const body = { mobile: e164 };
      const role = roleOverride ?? selectedRole;
      if (role) body.selected_role = role;

      const response = await axios.post(`${API}/auth/send-otp`, body);

      // Multi-account response — user must pick which role/account to sign in as.
      if (response.data?.multiple_accounts) {
        setMultipleAccounts(response.data.accounts || []);
        setOtpSent(false);
        setLoading(false);
        return;
      }

      setMultipleAccounts(null);
      setOtpSent(true);
      // The API uses dev_otp when SMS mode is enabled and mock_otp when the
      // provider is disabled. Support both so quick-login works in either
      // development configuration without exposing anything in real SMS mode.
      const devOtp = response.data.dev_otp || response.data.mock_otp || "";
      const sms = response.data.sms_sent === true;
      const smsErr = response.data.sms_error || "";
      setMockOtp(devOtp);
      setSmsSent(sms);
      setSmsError(smsErr);
      setResendCooldown(30);
      toast.success(`Demo OTP ready for ${e164}. Use the code shown below.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  // Cooldown timer for the Resend OTP button
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/verify-otp`, {
        mobile: e164,
        otp: otp,
        ...(selectedRole ? { selected_role: selectedRole } : {}),
      });

      const { access_token, user } = response.data;
      localStorage.setItem("token", access_token);
      localStorage.setItem("user", JSON.stringify(user));
      setUser(user);

      toast.success(`Login successful — ${roleLabel(user.role)} portal`);

      switch (user.role) {
        case "super_admin":
          navigate("/super-admin");
          break;
        case "chain_admin":
          navigate("/chain-admin");
          break;
        case "institute_admin":
          navigate("/school-admin");
          break;
        case "teacher":
          navigate("/teacher");
          break;
        case "student":
          navigate("/student");
          break;
        case "parent":
          navigate("/parent");
          break;
        case "staff":
          navigate("/staff");
          break;
        case "driver":
          navigate("/driver");
          break;
        case "training_manager":
          navigate("/training-manager");
          break;
        default:
          navigate("/");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleResetRequest = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/request-reset`, {
        email: resetEmail,
      });
      toast.success("Password reset link sent to your email");
      setShowReset(false);
      setResetEmail("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: Shield, text: "Enterprise-grade security" },
    { icon: Users, text: "Multi-role access" },
    { icon: Award, text: "Trusted by 500+ schools" }
  ];

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Visual */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-indigo-950 via-indigo-900 to-purple-900 relative overflow-hidden">
        {/* Pattern Overlay */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
        
        {/* Glowing Orbs */}
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-purple-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-indigo-400/20 rounded-full blur-3xl" />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20 group-hover:bg-white/20 transition-colors">
              <GraduationCap className="w-7 h-7 text-white" />
            </div>
            <span className="text-2xl font-bold text-white">ExpertLMS</span>
          </Link>

          {/* Middle Content */}
          <div className="space-y-8">
            <div>
              <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight mb-4">
                Welcome Back to the
                <span className="block text-indigo-300">Future of Education</span>
              </h1>
              <p className="text-lg text-indigo-200/80 max-w-md">
                Access your personalized dashboard and continue transforming educational experiences.
              </p>
            </div>

            {/* Feature List */}
            <div className="space-y-4">
              {features.map((feature, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-lg flex items-center justify-center border border-white/10">
                    <feature.icon className="w-5 h-5 text-indigo-300" />
                  </div>
                  <span className="text-white/90">{feature.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom Stats */}
          <div className="flex gap-8">
            <div>
              <div className="text-3xl font-bold text-white">1M+</div>
              <div className="text-indigo-300 text-sm">Active Users</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white">50+</div>
              <div className="text-indigo-300 text-sm">Countries</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white">99.9%</div>
              <div className="text-indigo-300 text-sm">Uptime</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12 bg-slate-50">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-950 rounded-xl flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-indigo-950">ExpertLMS</span>
            </Link>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 p-8 border border-slate-100">
            {!showReset ? (
              <>
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-indigo-950 mb-2">Sign In</h2>
                  <p className="text-slate-500">Choose your preferred login method</p>
                </div>

                <div className="grid grid-cols-2 gap-2 p-1 mb-6 rounded-xl bg-slate-100" role="tablist" aria-label="Login method">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={loginMethod === "password"}
                    onClick={() => setLoginMethod("password")}
                    className={`rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${loginMethod === "password" ? "bg-white text-indigo-950 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                    data-testid="password-login-tab"
                  >
                    Email &amp; Password
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={loginMethod === "otp"}
                    onClick={() => setLoginMethod("otp")}
                    className={`rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${loginMethod === "otp" ? "bg-white text-indigo-950 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                    data-testid="otp-login-tab"
                  >
                    Mobile OTP
                  </button>
                </div>

                {/* Password Login Form */}
                {loginMethod === "password" && (
                  <form onSubmit={handleLogin} className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-slate-700 font-medium">Email Address</Label>
                      <div className="relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                        <Input
                          id="email"
                          type="email"
                          placeholder="you@example.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          className="pl-12 h-12 bg-slate-50 border-slate-200 focus:border-indigo-500 focus:ring-indigo-500 rounded-xl"
                          data-testid="email-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="password" className="text-slate-700 font-medium">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                        <Input
                          id="password"
                          type="password"
                          placeholder="Enter your password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          className="pl-12 h-12 bg-slate-50 border-slate-200 focus:border-indigo-500 focus:ring-indigo-500 rounded-xl"
                          data-testid="password-input"
                        />
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="w-full h-12 bg-indigo-950 hover:bg-indigo-900 text-white rounded-xl font-semibold shadow-lg shadow-indigo-950/20 transition-all hover:shadow-xl"
                      disabled={loading}
                      data-testid="login-submit-btn"
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Signing in...
                        </span>
                      ) : "Sign In"}
                    </Button>

                    <button
                      type="button"
                      className="w-full text-center text-sm text-indigo-600 hover:text-indigo-700 font-medium py-2"
                      onClick={() => setShowReset(true)}
                      data-testid="forgot-password-btn"
                    >
                      Forgot your password?
                    </button>
                  </form>
                )}

                {/* OTP Login Form remains in source for later re-enablement. */}
                {loginMethod === "otp" && (
                  <div className="space-y-5">
                    {multipleAccounts && multipleAccounts.length > 0 ? (
                      // ── Multi-account picker: user's phone is shared by multiple roles ──
                      <div className="space-y-3" data-testid="multi-account-picker">
                        <div className="rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3">
                          <p className="text-sm font-semibold text-indigo-900">Multiple accounts found</p>
                          <p className="text-xs text-indigo-700 mt-0.5">This number is linked to {multipleAccounts.length} accounts. Choose which one to sign in as:</p>
                        </div>
                        <div className="space-y-2">
                          {multipleAccounts.map((acc, idx) => (
                            <button
                              key={`${acc.role}-${acc.email}-${idx}`}
                              type="button"
                              onClick={() => {
                                setSelectedRole(acc.role);
                                handleSendOTP(null, acc.role);
                              }}
                              disabled={loading}
                              className="w-full flex items-center justify-between rounded-xl border border-slate-200 bg-white hover:border-indigo-400 hover:bg-indigo-50 px-4 py-3 transition-colors text-left"
                              data-testid={`account-pick-${acc.role}-${idx}`}
                            >
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-semibold text-slate-800 truncate">{acc.name}</div>
                                <div className="text-xs text-slate-500 truncate">{acc.email}</div>
                              </div>
                              <span className="ml-3 inline-flex items-center rounded-full bg-indigo-100 text-indigo-800 text-[10px] font-semibold uppercase tracking-wide px-2 py-1">
                                {roleLabel(acc.role)}
                              </span>
                            </button>
                          ))}
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full h-12 border-slate-200 hover:bg-slate-50 rounded-xl"
                          onClick={() => {
                            setMultipleAccounts(null);
                            setSelectedRole("");
                          }}
                          data-testid="multi-cancel-btn"
                        >
                          Change Mobile Number
                        </Button>
                      </div>
                    ) : !otpSent ? (
                      <form onSubmit={handleSendOTP} className="space-y-5">
                        <div className="space-y-2">
                          <Label htmlFor="mobile" className="text-slate-700 font-medium">Mobile Number</Label>
                          <div className="flex">
                            <CountryCodePicker value={country} onChange={setCountry} />
                            <div className="relative flex-1">
                              <Smartphone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                              <Input
                                id="mobile"
                                type="tel"
                                placeholder={country?.code === "IN" ? "98765 43210" : "national number"}
                                value={mobile}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/[^0-9]/g, '');
                                  if (value.length <= 15) setMobile(value);
                                }}
                                inputMode="numeric"
                                pattern="[0-9]{4,15}"
                                required
                                className="pl-10 h-12 bg-slate-50 border-slate-200 focus:border-indigo-500 focus:ring-indigo-500 rounded-r-xl rounded-l-none"
                                data-testid="mobile-input"
                              />
                            </div>
                          </div>
                          <p className="text-xs text-slate-500 pl-1">
                            Pick your country then enter the number registered with your admin. We'll dial <b>{country?.dial}{mobile ? " " + mobile : ""}</b>.
                          </p>
                        </div>

                        <Button
                          type="submit"
                          className="w-full h-12 bg-indigo-950 hover:bg-indigo-900 text-white rounded-xl font-semibold shadow-lg shadow-indigo-950/20"
                          disabled={loading}
                        >
                          {loading ? (
                            <span className="flex items-center gap-2">
                              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                              Sending...
                            </span>
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Send OTP
                            </>
                          )}
                        </Button>
                      </form>
                    ) : (
                      <form onSubmit={handleVerifyOTP} className="space-y-5">
                        <div className="flex items-start gap-2 rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-800" data-testid="otp-demo-status">
                          <CheckCircle className="h-4 w-4 shrink-0 mt-0.5" />
                          <span>Demo login is ready. Use the one-time code shown below.</span>
                        </div>

                        {/* Dev / fallback OTP amber card */}
                        {mockOtp && (
                          <div className="rounded-xl border border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 px-4 py-3" data-testid="dev-otp-card">
                            <div className="flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <p className="text-[11px] uppercase tracking-wider font-semibold text-amber-700">Demo OTP — Quick Login</p>
                                <p className="text-2xl font-bold text-amber-900 tracking-[0.35em] font-mono" data-testid="dev-otp-value">{mockOtp}</p>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  navigator.clipboard.writeText(mockOtp);
                                  setOtp(mockOtp);
                                  toast.success("OTP copied & filled");
                                }}
                                className="shrink-0 flex items-center gap-1 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold px-3 py-2"
                                data-testid="dev-otp-copy-btn"
                              >
                                <Copy className="w-3.5 h-3.5" /> Use
                              </button>
                            </div>
                            <p className="text-[11px] text-amber-700 mt-1">Use this code to continue with the demo account.</p>
                          </div>
                        )}

                        <div className="space-y-2">
                          <Label htmlFor="otp" className="text-slate-700 font-medium">Enter OTP</Label>
                          <div className="relative">
                            <CheckCircle className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                            <Input
                              id="otp"
                              type="text"
                              placeholder="000000"
                              value={otp}
                              onChange={(e) => setOtp(e.target.value)}
                              required
                              maxLength={6}
                              className="pl-12 h-12 text-center text-xl tracking-[0.3em] bg-slate-50 border-slate-200 focus:border-indigo-500 focus:ring-indigo-500 rounded-xl font-semibold"
                              data-testid="otp-input"
                            />
                          </div>
                          <p className="text-xs text-slate-500 pl-1">OTP sent to {e164}</p>
                        </div>

                        <Button
                          type="submit"
                          className="w-full h-12 bg-indigo-950 hover:bg-indigo-900 text-white rounded-xl font-semibold shadow-lg shadow-indigo-950/20"
                          disabled={loading}
                          data-testid="verify-otp-btn"
                        >
                          {loading ? "Verifying..." : "Verify & Sign In"}
                        </Button>

                        <div className="grid grid-cols-2 gap-3">
                          <Button
                            type="button"
                            variant="outline"
                            className="h-12 border-slate-200 hover:bg-slate-50 rounded-xl disabled:opacity-50"
                            onClick={() => handleSendOTP()}
                            disabled={resendCooldown > 0 || loading}
                            data-testid="resend-otp-btn"
                          >
                            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend OTP"}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            className="h-12 border-slate-200 hover:bg-slate-50 rounded-xl"
                            onClick={() => {
                              setOtpSent(false);
                              setOtp("");
                              setMockOtp("");
                              setSmsSent(false);
                              setSmsError("");
                              setResendCooldown(0);
                              setMultipleAccounts(null);
                              setSelectedRole("");
                            }}
                            data-testid="change-mobile-btn"
                          >
                            Change Number
                          </Button>
                        </div>
                      </form>
                    )}
                  </div>
                )}
              </>
            ) : (
              /* Password Reset Form */
              <div>
                <button
                  onClick={() => setShowReset(false)}
                  className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-6 text-sm font-medium"
                  data-testid="back-to-login-btn"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to login
                </button>

                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-indigo-950 mb-2">Reset Password</h2>
                  <p className="text-slate-500">Enter your email and we'll send you a new password</p>
                </div>

                <form onSubmit={handleResetRequest} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="reset-email" className="text-slate-700 font-medium">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                      <Input
                        id="reset-email"
                        type="email"
                        placeholder="you@example.com"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        required
                        className="pl-12 h-12 bg-slate-50 border-slate-200 focus:border-indigo-500 focus:ring-indigo-500 rounded-xl"
                        data-testid="reset-email-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 bg-indigo-950 hover:bg-indigo-900 text-white rounded-xl font-semibold shadow-lg shadow-indigo-950/20"
                    disabled={loading}
                    data-testid="reset-submit-btn"
                  >
                    {loading ? "Sending..." : "Send New Password"}
                  </Button>
                </form>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="text-sm text-slate-500">
              Don't have an account?{" "}
              <Link to="/" className="text-indigo-600 hover:text-indigo-700 font-medium">
                Contact your administrator
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
